import API from "../api/api";

// VerySoul Gift — API calls (backend: docs/plans/gift-experience.md).
// Same response handling as the other service files: resolve with the axios
// response (or the error) and let the caller read data.data.status.

const config = (token) => {
  return {
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
};

const call = (request) =>
  request
    .then((data) => {
      return data;
    })
    .catch((err) => {
      return err;
    });

// ---- Purchaser (logged in) ----

export const getGiftOptions = async () => {
  let token = localStorage.getItem("access_token");
  return call(API.get(`gifts/options`, config(token)));
};

export const createGift = async (body) => {
  let token = localStorage.getItem("access_token");
  return call(API.post(`gifts`, body, config(token)));
};

export const confirmGift = async (sessionId) => {
  let token = localStorage.getItem("access_token");
  return call(API.get(`gifts/confirm?session_id=${encodeURIComponent(sessionId)}`, config(token)));
};

export const getMyGifts = async () => {
  let token = localStorage.getItem("access_token");
  return call(API.get(`gifts`, config(token)));
};

export const getMyGift = async (id) => {
  let token = localStorage.getItem("access_token");
  return call(API.get(`gifts/${id}`, config(token)));
};

// Card wording: the message catalog, and saving the giver's choice for a gift.
export const getGiftCardCatalog = async () => {
  let token = localStorage.getItem("access_token");
  return call(API.get(`gifts/card-catalog`, config(token)));
};

// body: { option_id, name?, from_name? } or { custom: { line1, line2, line3 }, from_name? }
export const saveGiftCardDesign = async (id, body) => {
  let token = localStorage.getItem("access_token");
  return call(API.post(`gifts/${id}/card-design`, body, config(token)));
};

// Giver's preview: the mediums a gift of this maximum would offer (nothing is saved).
export const previewGiftMediums = async (maxPrice) => {
  let token = localStorage.getItem("access_token");
  return call(API.get(`gifts/preview?max_price=${encodeURIComponent(maxPrice)}`, config(token)));
};

// "Send this gift": body { channel, email, recipient_name, show_name, sender_name, recaptcha_token }.
export const sendGiftCard = async (id, body) => {
  let token = localStorage.getItem("access_token");
  return call(API.post(`gifts/${id}/send`, body, config(token)));
};

// The giver cancels an unused or paused gift.
export const cancelMyGift = async (id) => {
  let token = localStorage.getItem("access_token");
  return call(API.post(`gifts/${id}/cancel`, {}, config(token)));
};

// "Do you still want to give a gift?" answered from My Gifts (keep: true = Yes).
export const decideMyGift = async (id, keep) => {
  let token = localStorage.getItem("access_token");
  return call(API.post(`gifts/${id}/decision`, { keep }, config(token)));
};

// Public decision page (/gift-decision/:token), opened from the purchaser's email.
export const getGiftDecision = async (decisionToken) =>
  call(API.get(`gift-decision/${encodeURIComponent(decisionToken)}`, { headers: { "Content-type": "application/json" } }));

export const answerGiftDecision = async (decisionToken, keep) =>
  call(API.post(`gift-decision/${encodeURIComponent(decisionToken)}`, { keep }, { headers: { "Content-type": "application/json" } }));

// Language list for "About you". Sent without a login token: a stale saved token
// must not block this public list for a recipient.
export const getGiftLanguages = async () =>
  call(API.get(`language-data`, { headers: { "Content-type": "application/json" } }));

// ---- Response helpers (this build doesn't support ?. / ??) ----

export const isOk = (res) => Boolean(res && res.data && res.data.status);

export const messageOf = (res, fallback) => (res && res.data && res.data.message) || fallback;

// ---- Recipient (public; a logged-in user's token is sent if present so
// they book as themselves) ----

const optionalAuth = () => {
  const token = localStorage.getItem("access_token");
  return token ? config(token) : { headers: { "Content-type": "application/json" } };
};

export const validateGiftCode = async (code, recaptchaToken) =>
  call(API.post(`gift-redeem/validate`, { code, recaptcha_token: recaptchaToken }, optionalAuth()));

// options: { languages: [ids] } from "About you", or { email } for an existing
// account (its languages are used once a code was sent to it).
export const getGiftMediums = async (token, options = {}) =>
  call(
    API.get(`gift-redeem/mediums`, {
      ...optionalAuth(),
      params: { token, languages: options.languages && options.languages.length ? options.languages : undefined, email: options.email || undefined },
    })
  );

export const getGiftCalendar = async (params) =>
  call(API.get(`gift-redeem/calendar`, { ...optionalAuth(), params }));

export const getGiftSlots = async (params) =>
  call(API.get(`gift-redeem/slots`, { ...optionalAuth(), params }));

export const sendGiftVerificationCode = async (token, email, recaptchaToken) =>
  call(API.post(`gift-redeem/send-code`, { token, email, recaptcha_token: recaptchaToken }, optionalAuth()));

export const bookGiftSession = async (body) => call(API.post(`gift-redeem/book`, body, optionalAuth()));

// ---- Purchaser pay page (public; the emailed token is the credential) ----

export const getGiftPayment = async (payToken) =>
  call(API.get(`gift-pay/${encodeURIComponent(payToken)}`, { headers: { "Content-type": "application/json" } }));

export const startGiftCheckout = async (payToken, method) =>
  call(API.post(`gift-pay/${encodeURIComponent(payToken)}/checkout`, { method }, { headers: { "Content-type": "application/json" } }));

// ---- Admin ----

const admin = (method, url, body) => {
  const token = localStorage.getItem("access_token");
  return method === "get" || method === "delete"
    ? call(API[method](url, config(token)))
    : call(API[method](url, body || {}, config(token)));
};

export const adminGetGifts = async (params) => {
  const query = new URLSearchParams(params).toString();
  return admin("get", `admin/gifts?${query}`);
};

export const adminGetGift = async (id) => admin("get", `admin/gifts/${id}`);

// action: suspend | disable | reactivate | expire | block-purchaser (reason required) | resend-receipt
export const adminGiftAction = async (id, action, reason) =>
  admin("post", `admin/gifts/${id}/${action}`, reason ? { reason } : {});

// body: { reason, days } to extend by days, or { reason, expires_at } to set the date.
export const adminExtendGift = async (id, body) => admin("post", `admin/gifts/${id}/extend`, body);

export const adminGetGiftBlocks = async () => admin("get", `admin/gifts/blocks`);

export const adminRemoveGiftBlock = async (blockId) => admin("delete", `admin/gifts/blocks/${blockId}`);

export const adminGetGiftSettings = async () => admin("get", `admin/gift-settings`);

export const adminSaveGiftSettings = async (fee) => admin("post", `admin/gift-settings`, { fee });
